import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ChatMessage } from "../../interfaces/SupportObject";
import { resetAllState } from "./resetAllState";
import { askSupport } from "../../services/thunks/SupportThunk";

// The message list IS the conversation memory (the backend is stateless — see
// SupportObject.ts). Deliberately ephemeral: lives only for this Redux session
// and is wiped on logout via resetAllState. No persistence middleware, no
// localStorage — a refresh clears it, by design.
//
// Capped at MAX_MESSAGES so a very long help session doesn't grow the store
// (or the `history` payload built from it) unboundedly.
//
// NOTE — deliberate deviation from the repo's usual slice shape: most slices
// (e.g. SubscriptionsDb, OrdersDb, CartDb) key loadingMap/errorMap/successMap
// by operation/entity id because they track several independent, possibly
// concurrent async operations over a collection. This slice tracks exactly
// one thing — a single stateless Q&A round-trip for one conversation, never
// concurrent with itself (the composer/chips are disabled while `loading` is
// set) — so a flat `loading`/`error` string is enough and there's no entity
// to key a map by. There's also no `successMap`: success is just "the answer
// got pushed onto `messages`", nothing else needs to observe it. Don't copy
// this shape for a new slice unless it has the same single-operation,
// non-keyed profile — everything else in the repo should still follow the
// mapped convention.
const MAX_MESSAGES = 20; // 10 QA pairs = 20 messages (1 user + 1 assistant each)

interface SupportChatState {
  messages: ChatMessage[];
  /** Non-null while a request is in flight — drives the typing indicator and
   *  disables the composer/chips so a stateless backend never sees overlapping
   *  requests for the same conversation. */
  loading: string | null;
  /** Tracked internally on every rejection, but deliberately has no selector:
   *  askSupport.rejected always also flips the specific user message to
   *  "error" status (see the reducer below), and MessageBubble's per-bubble
   *  Retry affordance already covers every case this could report. A
   *  selector here would just be a second, redundant surface for the same
   *  failure — add one back only if a future failure mode needs a
   *  conversation-wide banner that isn't tied to one message. */
  error: string | null;
}

const initialState: SupportChatState = {
  messages: [],
  loading: null,
  error: null,
};

/** Keeps only the most recent MAX_MESSAGES, dropping oldest first. */
function capMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages.length > MAX_MESSAGES ? messages.slice(messages.length - MAX_MESSAGES) : messages;
}

const supportChatSlice = createSlice({
  name: "supportChat",
  initialState,
  reducers: {
    /** Optimistically appends the user's message before the request goes out. */
    appendUserMessage: (state, action: PayloadAction<ChatMessage>) => {
      // Only the newest assistant answer should show follow-up chips — once a
      // new turn is sent, older ones are stale next-question suggestions.
      state.messages.forEach((m) => {
        if (m.role === "assistant") m.followUps = undefined;
      });
      state.messages.push(action.payload);
      state.messages = capMessages(state.messages);
      state.error = null;
    },
    /** Flips a failed user message back to "sending" right before its retry
     *  request goes out — reuses the same message id (rather than removing +
     *  re-appending) so askSupport's fulfilled/rejected handlers, which key
     *  off meta.arg.userMessageId, land on this exact bubble either way. */
    retryMessage: (state, action: PayloadAction<{ id: string }>) => {
      const message = state.messages.find((m) => m.id === action.payload.id);
      if (message) message.status = "sending";
      state.error = null;
    },
    /** Wipes the conversation (e.g. re-opening the Help tab for a fresh session). */
    clearConversation: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(askSupport.pending, (state) => {
        state.loading = "Thinking…";
        state.error = null;
      })
      .addCase(askSupport.fulfilled, (state, action) => {
        state.loading = null;
        const sent = state.messages.find((m) => m.id === action.meta.arg.userMessageId);
        if (sent) sent.status = undefined; // optimistic bubble confirmed delivered
        state.messages.push({
          id: `a_${action.meta.arg.userMessageId}`,
          role: "assistant",
          content: action.payload.answer,
          followUps: action.payload.suggestedFollowUps,
          videos: action.payload.videos,
        });
        state.messages = capMessages(state.messages);
      })
      .addCase(askSupport.rejected, (state, action) => {
        state.loading = null;
        state.error = (action.payload as string) ?? "Support is unavailable.";
        // Flip the optimistic user bubble to "error" so the Retry affordance shows.
        const failed = state.messages.find((m) => m.id === action.meta.arg.userMessageId);
        if (failed) failed.status = "error";
      })
      .addCase(resetAllState, () => initialState);
  },
});

export const { appendUserMessage, retryMessage, clearConversation } = supportChatSlice.actions;

export const selectSupportMessages = (state: any): ChatMessage[] => state.supportChat.messages;
export const selectSupportLoading = (state: any): string | null => state.supportChat.loading;

export default supportChatSlice.reducer;
